import { useState, useEffect, useCallback, memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Search, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { useImageStore } from "@/stores/imageStore";
import { imageService } from "@/services/imageService";

interface TinyMCEImagePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (payload: {
    url: string;
    alt?: string;
    title?: string;
    caption?: string;
    linkImage?: string;
    width?: number;
    height?: number;
    rel?: 'nofollow' | 'noreferrer' | 'noopener' | 'sponsored' | 'ugc';
    target?: '_self' | '_blank' | '_parent' | '_top';
    id?: string;
  }) => void;
}

export const TinyMCEImagePicker = memo(function TinyMCEImagePicker({
  isOpen,
  onClose,
  onSelect,
}: TinyMCEImagePickerProps) {
  const {
    items: images,
    isLoading,
    isLoadingMore,
    hasMore,
    pagination,
    fetch: fetchImages,
    fetchMore: fetchMoreImages,
  } = useImageStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [altText, setAltText] = useState("");
  const [titleText, setTitleText] = useState("");
  const [captionText, setCaptionText] = useState("");
  const [linkImage, setLinkImage] = useState("");
  const [widthVal, setWidthVal] = useState<number | undefined>(undefined);
  const [heightVal, setHeightVal] = useState<number | undefined>(undefined);
  const [relVal, setRelVal] = useState<'nofollow' | 'noreferrer' | 'noopener' | 'sponsored' | 'ugc' | undefined>(undefined);
  const [targetVal, setTargetVal] = useState<'_self' | '_blank' | '_parent' | '_top' | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newUrl, setNewUrl] = useState("");

  // Fetch images when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchImages({ page: 1, limit: 20 });
    } else {
      // clear lightweight local state to avoid stale UI when reopen
      setSelectedImage(null);
      setAltText("");
      setSearchTerm("");
      setNewFile(null);
      setNewUrl("");
    }
  }, [isOpen, fetchImages]);

  // Load more images
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      fetchMoreImages({ search: searchTerm });
    }
  }, [hasMore, isLoadingMore, fetchMoreImages, searchTerm]);

  // Search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        fetchImages({ page: 1, limit: 20, search: searchTerm });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen, fetchImages]);

  // No need to filter since search is handled by the API
  const filteredImages = images || [];

  // Debug logging
  if (isOpen) {
    console.log("TinyMCE Image Picker Debug:", {
      isOpen,
      images: images?.length || 0,
      filteredImages: filteredImages.length,
      isLoading,
      isLoadingMore,
      hasMore,
      pagination,
      searchTerm,
    });
  }

  const handleImageSelect = (image: any) => {
    setSelectedImage(image.url);
    setSelectedImageId(image._id || null);
    setAltText(image.alt || image.title || "");
    setTitleText(image.title || "");
    setCaptionText(image.caption || "");
    setLinkImage(image.linkImage || "");
    setWidthVal(image.width || undefined);
    setHeightVal(image.height || undefined);
    setRelVal(image.rel || undefined);
    setTargetVal(image.target || undefined);
  };

  const handleConfirm = () => {
    if (selectedImage) {
      onSelect({ url: selectedImage, alt: altText, title: titleText, caption: captionText, linkImage, width: widthVal, height: heightVal, rel: relVal, target: targetVal, id: selectedImageId || undefined });
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
    setSelectedImageId(null);
    setAltText("");
    setTitleText("");
    setCaptionText("");
    setLinkImage("");
    setWidthVal(undefined);
    setHeightVal(undefined);
    setRelVal(undefined);
    setTargetVal(undefined);
    setSearchTerm("");
    setNewFile(null);
    setNewUrl("");
    onClose();
  };

  const handleCreateFromFile = async () => {
    if (!newFile) return;
    try {
      setUploading(true);
      const resp: any = await imageService.upload(newFile, {
        title: newFile.name.replace(/\.[^/.]+$/, ""),
        alt: newFile.name,
        caption: "",
        linkImage: "",
        rel: "nofollow",
        target: "_self",
      });
      const data = resp?.data?.data?.data || resp?.data;
      if (data?.url) {
        setSelectedImage(data.url);
        setAltText(data.alt || data.title || "");
        // refresh list
        fetchImages({ page: 1, limit: 20 });
        // clear chosen file UI
        setNewFile(null);
        const inputEl = document.getElementById("picker-upload-input") as HTMLInputElement | null;
        if (inputEl) inputEl.value = "";
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFromUrl = async () => {
    if (!newUrl.trim()) return;
    try {
      setUploading(true);
      const filename = newUrl.split("/").pop() || `image-${Date.now()}.png`;
      const resp: any = await imageService.createFromUrl({
        url: newUrl.trim(),
        title: filename,
        alt: filename,
        linkImage: "",
        rel: "nofollow",
        target: "_self",
      });
      const data = resp?.data?.data?.data || resp?.data;
      if (data?.url) {
        setSelectedImage(data.url);
        setAltText(data.alt || data.title || "");
        setSelectedImageId(data._id || null);
        setTitleText(data.title || "");
        setCaptionText(data.caption || "");
        setLinkImage(data.linkImage || "");
        setWidthVal(data.width || undefined);
        setHeightVal(data.height || undefined);
        setRelVal(data.rel || undefined);
        setTargetVal(data.target || undefined);
        setNewUrl("");
        fetchImages({ page: 1, limit: 20 });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Chọn hình ảnh từ bộ sưu tập
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Search */}
          <div className="mb-4 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Tìm kiếm hình ảnh..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

        {/* Quick Create */}
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="picker-upload-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setNewFile(e.target.files?.[0] || null)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => document.getElementById("picker-upload-input")?.click()}
              className="whitespace-nowrap"
            >
              Chọn file
            </Button>
            <Button
              size="sm"
              onClick={handleCreateFromFile}
              disabled={!newFile || uploading}
              className="whitespace-nowrap"
            >
              {uploading ? "Đang tải..." : "Tải ảnh mới"}
            </Button>
            <span className="text-xs text-gray-500 truncate max-w-[240px]">
              {newFile ? newFile.name : "Chưa chọn file"}
            </span>
            {newFile && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setNewFile(null);
                  const inputEl = document.getElementById("picker-upload-input") as HTMLInputElement | null;
                  if (inputEl) inputEl.value = "";
                }}
                className="text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
              >
                Xóa
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Dán link ảnh (http...)"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleCreateFromUrl}
              disabled={!newUrl || uploading}
              className="whitespace-nowrap"
            >
              {uploading ? "Đang tạo..." : "Tạo từ URL"}
            </Button>
            {newUrl && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewUrl("")}
                className="text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
              >
                Xóa
              </Button>
            )}
          </div>
        </div>

        {/* Image Grid with Infinite Scroll */}
          <div className="flex-1 overflow-y-auto scrollbar-gentle">
            {isLoading && filteredImages.length === 0 ? (
              <div className="flex items-center justify-center h-40">
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Đang tải hình ảnh...
                </div>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="flex items-center justify-center h-40">
                <div className="text-center text-gray-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Không tìm thấy hình ảnh nào</p>
                  {searchTerm && (
                    <p className="text-sm mt-1">
                      Thử tìm kiếm với từ khóa khác
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredImages.map((image) => (
                  <div
                    key={image._id}
                    className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 hover:shadow-lg ${
                      selectedImage === image.url
                        ? "border-blue-500 ring-4 ring-blue-100 shadow-lg scale-105"
                        : "border-gray-200 hover:border-gray-300 hover:scale-105"
                    }`}
                    onClick={() => handleImageSelect(image)}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={image.url}
                        alt={image.alt || image.title || ""}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                        loading="lazy"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                      {/* Selection indicator */}
                      {selectedImage === image.url && (
                        <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                          <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg animate-pulse">
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}

                      {/* Image info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <p className="text-white text-xs truncate">
                          {image.filename || image.title || "Untitled"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <div className="col-span-full flex items-center justify-center py-8">
                    <Button
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      variant="outline"
                      className="px-8"
                    >
                      {isLoadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Đang tải...
                        </>
                      ) : (
                        "Tải thêm hình ảnh"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fixed Bottom Section */}
        <div className="flex-shrink-0 mt-4 space-y-4">
          {selectedImage && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="w-full flex items-start gap-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-blue-300 shadow-lg flex-shrink-0">
                    <img src={selectedImage} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <Label className="text-sm">Tiêu đề</Label>
                      <Input value={titleText} onChange={(e) => setTitleText(e.target.value)} placeholder="Tiêu đề ảnh" />
                    </div>
                    <div>
                      <Label className="text-sm">Alt Text (SEO)</Label>
                      <Input value={altText} onChange={(e) => setAltText(e.target.value)} placeholder="Mô tả thay thế" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm">Caption</Label>
                    <Input value={captionText} onChange={(e) => setCaptionText(e.target.value)} placeholder="Chú thích ảnh" />
                  </div>
                  <div>
                    <Label className="text-sm">Link ảnh (tùy chọn)</Label>
                    <Input value={linkImage} onChange={(e) => setLinkImage(e.target.value)} placeholder="https://example.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Width</Label>
                      <Input type="number" value={widthVal ?? ''} onChange={(e) => setWidthVal(e.target.value ? Number(e.target.value) : undefined)} placeholder="auto" />
                    </div>
                    <div>
                      <Label className="text-sm">Height</Label>
                      <Input type="number" value={heightVal ?? ''} onChange={(e) => setHeightVal(e.target.value ? Number(e.target.value) : undefined)} placeholder="auto" />
                    </div>
                    <div>
                      <Label className="text-sm">Rel</Label>
                      <Select value={relVal ?? ''} onValueChange={(v) => setRelVal((v as any) || undefined)}>
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="nofollow" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nofollow">nofollow</SelectItem>
                          <SelectItem value="noreferrer">noreferrer</SelectItem>
                          <SelectItem value="noopener">noopener</SelectItem>
                          <SelectItem value="sponsored">sponsored</SelectItem>
                          <SelectItem value="ugc">ugc</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Target</Label>
                      <Select value={targetVal ?? ''} onValueChange={(v) => setTargetVal((v as any) || undefined)}>
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="_self" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_self">_self</SelectItem>
                          <SelectItem value="_blank">_blank</SelectItem>
                          <SelectItem value="_parent">_parent</SelectItem>
                          <SelectItem value="_top">_top</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={async () => {
                      if (!selectedImageId) return;
                      try {
                        setUploading(true);
                        await imageService.update(selectedImageId, {
                          title: titleText || undefined,
                          alt: altText || undefined,
                          caption: captionText || undefined,
                          linkImage: linkImage || undefined,
                          width: widthVal || undefined,
                          height: heightVal || undefined,
                          rel: relVal || undefined,
                          target: targetVal || undefined,
                        } as any);
                        await fetchImages({ page: 1, limit: 20, search: searchTerm });
                      } finally {
                        setUploading(false);
                      }
                    }} disabled={!selectedImageId || uploading}>
                      {uploading ? "Đang lưu..." : "Lưu thông tin ảnh"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              {filteredImages.length > 0 && (
                <>
                  Hiển thị {filteredImages.length} hình ảnh
                  {pagination?.total &&
                    pagination.total > filteredImages.length && (
                      <span> / {pagination.total} tổng cộng</span>
                    )}
                </>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose} className="px-6">
                <X className="h-4 w-4 mr-2" />
                Hủy
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedImage}
                className="bg-blue-600 hover:bg-blue-700 px-6"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Chọn hình ảnh
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

TinyMCEImagePicker.displayName = "TinyMCEImagePicker";
